/**
 *
 * Registration
 *
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { Set } from 'immutable';
import { addDays } from 'utils/date-fns/index.js';
import DatePicker from 'components/interaction/DatePicker/index.jsx';
import { FormattedMessage } from 'react-intl';
import Select from 'components/tags/Select/index.jsx';
import { defaultTime } from 'utils/eventTimes.js';
import messages from './messages.js';
import ActionButtons from '../ActionButtons/index.jsx';
import useSaveMeetingSet from '../../hooks/useSaveMeetingSet.js';

const Wrapper = styled.div`
  display: flex;
  justify-content: space-around;
  & > form {
    max-width: 480px;
    margin-bottom: 15px;
    & > div {
      margin-bottom: 1rem;
    }
  }
  label {
    font-weight: 500;
  }
  label + span {
    margin-left: 0.5rem;
  } 
  .rdt input {
    max-width: 10rem;
  }
  select {
    max-width: 20rem;
  }
  .label-help {
    color: ${(p) => p.theme.gray};
  }
`;

function Registration(props) {
  const { meetingSet } = props;
  const isSet = (key) => ![null, undefined].includes(meetingSet[key]);
  const [data, setData] = useState({
    registerStart: isSet('registerStart') ? meetingSet.registerStart : defaultTime(),
    registerEnd: isSet('registerEnd') ? meetingSet.registerEnd : undefined,
    announcementFlag: isSet('announcementFlag') ? meetingSet.announcementFlag : false,
    announcementInDays: isSet('announcementInDays') ? meetingSet.announcementInDays + 1 : 0,
  });
  const [changed, setChanged] = useState(false);
  const [errors, setErrors] = useState(Set());
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const saveMeetingSet = useSaveMeetingSet();
  useEffect(() => {
    const activeTab = document.querySelector('main nav button.active');
    if (activeTab) {
      activeTab.focus();
    }
  }, []);

  const handleChange = (key) => (ev) => {
    const value = getValue(ev);
    setData({ ...data, [key]: value });
    setChanged(true);
    const valid = validate(key, value);
    if (valid) {
      setErrors(errors.delete(key));
    } else if (valid === false) {
      setErrors(errors.add(key));
    }
  };

  function validate(key, value) {
    switch (key) {
      case 'registerStart':
        return Boolean(value);
      case 'registerEnd':
        return !value || value > data.registerStart;
      case 'announcementInDays':
        return !data.announcementFlag || [1, 2, 8].includes(Number(value));
      case 'announcementFlag':
        if (!value) {
          setErrors(errors.delete('announcementInDays'));
        }
        return undefined;
      default:
        return undefined;
    }
  }
  function handleSave(complete) {
    const allData = {
      ...meetingSet,
      ...data,
      complete: meetingSet.complete || complete,
      announcementInDays: data.announcementInDays ? data.announcementInDays - 1 : undefined,
    };
    setChanged(false);
    return saveMeetingSet(allData);
  }

  function validateAll() {
    const errorKeys = ['registerStart', 'registerEnd', 'announcementInDays'].filter((key) => !validate(key, data[key]));
    errorKeys.forEach((key) => setErrors(errors.add(key)));
    return errorKeys.length === 0;
  }

  function handleSubmit(ev) {
    if (ev) {
      ev.preventDefault();
    }
    if (!validateAll()) {
      return;
    }
    handleSave(true)
      .then(() => {
        const uplink = pathname.replace(/\/planner.*$/, '');
        navigate(uplink);
      });
  }

  const validSubmit = () => errors.size === 0 ? handleSubmit : undefined;

  function handleFocusEnd() {
    const { registerStart, registerEnd } = data;
    if (!registerEnd) {
      handleChange('registerEnd')(addDays(registerStart, 1));
      setChanged(true);
    }
  }

  const handleClose = !changed ? undefined : (flag) => {
    if (flag) {
      handleSave(false);
    }
  };

  const errorClass = (key) => errors.has(key) ? 'has-error' : '';

  const announceOptions = [
    { value: 1, label: messages.announceDirect },
    { value: 2, label: messages.announceOneDay },
    { value: 8, label: messages.announceOneWeek },
  ];
  const { registerStart, registerEnd, announcementFlag, announcementInDays } = data;
  return (
    <Wrapper>
      <form onSubmit={handleSubmit}>
        <Form.Group className={errorClass('registerStart')}>
          <div className="mb-1">
            <label htmlFor="registerStart"><FormattedMessage {...messages.startLabel} /></label>
            <span className="label-help"><FormattedMessage {...messages.startHelp} /></span>
          </div>
          <DatePicker
            id="registerStart"
            value={registerStart}
            showTimeSelect
            selectsStart
            startDate={registerStart}
            endDate={registerEnd}
            maxDate={registerEnd}
            onChange={handleChange('registerStart')}
            required
          />
        </Form.Group>
        <Form.Group className={errorClass('registerEnd')}>
          <div className="mb-1">
            <label htmlFor="registerEnd"><FormattedMessage {...messages.endLabel} /></label>
            <span className="label-help"><FormattedMessage {...messages.endHelp} /></span>
          </div>
          <DatePicker
            id="registerEnd"
            value={registerEnd}
            showTimeSelect
            selectsEnd
            isClearable
            startDate={registerStart}
            endDate={registerEnd}
            onFocus={handleFocusEnd}
            onChange={handleChange('registerEnd')}
          />
          <div className="form-text mt-2"><FormattedMessage {...messages.endMoreHelp} /></div>
        </Form.Group>
        <Form.Group className={errorClass('announcementInDays')}>
          <Form.Check
            type="checkbox"
            checked={announcementFlag}
            value={announcementFlag}
            onChange={handleChange('announcementFlag')}
            label={<FormattedMessage {...messages.announceText} />}
            className="mb-2"
          />
          <Select
            value={announcementInDays}
            onChange={handleChange('announcementInDays')}
            disabled={!announcementFlag}
            options={announceOptions}
          />
        </Form.Group>
        <ActionButtons onClose={handleClose} onSubmit={validSubmit()} isFinal />
      </form>
    </Wrapper>
  );
}

Registration.propTypes = {
  meetingSet: PropTypes.object,
};

export default Registration;

function getValue(ev) {
  if (!ev) {
    return ev;
  }
  if (ev.target) {
    if (ev.target.checked !== undefined) {
      return ev.target.checked;
    }
    return ev.target.value;
  }
  return typeof ev === 'string' ? ev.trim() : ev;
}
